import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from '@/hooks/use-toast';
import { memberTypesApi, publicPlayingLevelsApi, publicPlayingPositionsApi } from '@/lib/api';
import { MemberType } from '@/types';
import LoadingSpinner from '@/components/shared/LoadingSpinner';
import { Textarea } from '@/components/ui/textarea';
import { format } from 'date-fns';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { CalendarIcon } from '@radix-ui/react-icons';
import { Camera } from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { InfoCircledIcon } from "@radix-ui/react-icons";
import MemberFormTemplate from '@/components/shared/MemberFormTemplate';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// Playing levels will be fetched from API

const registerSchema = z.object({
  // Required Fields
  first_name: z.string().min(2, 'First name is required').max(255, 'First name too long'),
  last_name: z.string().min(2, 'Last name is required').max(255, 'Last name too long'),
  email: z.string().email('Invalid email address'),
  phone: z.string().min(10, 'Phone number is required'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  password_confirmation: z.string(),
  terms_accepted: z.boolean().refine(val => val === true, {
    message: 'You must accept the terms and conditions',
  }),
  consent: z.boolean().refine(val => val === true, {
    message: 'You must give consent to proceed',
  }),

  // Optional Personal Information
  dob: z.string().optional(),
  nationality: z.string().max(100, 'Nationality too long').optional(),
  id_passport: z.string().max(50, 'ID/Passport number too long').optional(),
  residential_address: z.string().max(1000, 'Address too long').optional(),
  occupation: z.string().max(255, 'Occupation too long').optional(),
  employer: z.string().max(255, 'Employer name too long').optional(),

  // Emergency Contact
  emergency_contact_name: z.string().max(255, 'Contact name too long').optional(),
  emergency_contact_phone: z.string().optional(),
  emergency_contact_relationship: z.string().max(100, 'Relationship too long').optional(),

  // Basketball Info
  playing_position_id: z.string().optional(),
  playing_level_id: z.string().optional(),
  playing_level: z.string().optional(),
  jersey_number: z.string().transform(val => val ? parseInt(val) : undefined)
    .optional()
    .refine(val => !val || (val >= 1 && val <= 99), {
      message: 'Jersey number must be between 1 and 99',
    }),
  member_type_id: z.string().optional(),
  membership_type: z.string().nonempty('Please select a membership type'),

  // Health & Medical
  medical_info: z.string().max(2000, 'Medical info too long').optional(),
  health_conditions: z.string().max(1000, 'Health conditions too long').optional(),
  allergies: z.string().max(1000, 'Allergies too long').optional(),
  medical_conditions: z.string().max(1000, 'Medical conditions too long').optional(),
  insurance: z.string().max(255, 'Insurance info too long').optional(),
  insurance_provider: z.string().max(255, 'Provider name too long').optional(),

  // Payment Settings
  auto_payment: z.boolean().optional(),
  payment_phone: z.string().optional(),
  payment_provider: z.string().optional(),
  auto_renew: z.boolean().optional(),
  photo: z.any().optional(),
}).refine(data => data.password === data.password_confirmation, {
  message: 'Passwords do not match',
  path: ['password_confirmation'],
});

const SECTIONS = [
  'personal',
  'membership',
  'emergency',
  'medical',
  'consent',
  'preview',
];

const OgLogoHeader = () => (
  <Link to="/" className="flex flex-col items-center mb-6 cursor-pointer">
    <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-basketball-blue-500 rounded-full flex items-center justify-center mb-2">
      <span className="text-white text-3xl">🏀</span>
    </div>
    <h1 className="text-3xl font-bold text-basketball-blue-700 mb-1">Join OG Basketball Club</h1>
    <p className="text-gray-500 text-base">Complete your registration to become a member</p>
  </Link>
);

const Register = () => {
  const { register: registerUser } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [memberTypes, setMemberTypes] = useState<MemberType[]>([]);
  const [playingLevels, setPlayingLevels] = useState<any[]>([]);
  const [playingPositions, setPlayingPositions] = useState<any[]>([]);
  const [isTypesLoading, setIsTypesLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [step, setStep] = useState(0);

  const { control, handleSubmit, formState: { errors }, watch, getValues, trigger, setValue } = useForm({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      password: '',
      password_confirmation: '',
      member_type_id: '',
      membership_type: '',
      playing_level: '',
      playing_position_id: '',
      jersey_number: '',
      dob: '',
      nationality: '',
      id_passport: '',
      residential_address: '',
      occupation: '',
      employer: '',
      emergency_contact_name: '',
      emergency_contact_phone: '',
      emergency_contact_relationship: '',
      medical_info: '',
      health_conditions: '',
      allergies: '',
      medical_conditions: '',
      insurance: '',
      insurance_provider: '',
      auto_payment: false,
      payment_phone: '',
      payment_provider: 'auto',
      auto_renew: false,
      terms_accepted: false,
      consent: false,
    },
  });

  const stepLabels = ["Personal Information", "Membership Details", "Emergency Contact", "Medical Information", "Consent & Declaration", "Preview"];

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsTypesLoading(true);
        console.log('Fetching member types, playing levels, and positions using public endpoints...');
        
        // Fetch member types
        let memberTypesResponse: any;
        try {
          memberTypesResponse = await memberTypesApi.getAll();
        } catch (error) {
          console.error('Member types endpoint failed:', error);
          memberTypesResponse = [];
        }
        
        // Fetch playing levels
        let playingLevelsResponse: any;
        try {
          playingLevelsResponse = await publicPlayingLevelsApi.getAll();
        } catch (error) {
          console.error('Playing levels endpoint failed:', error);
          // Use fallback hardcoded values
          playingLevelsResponse = [
            { id: 1, name: 'Recreational', description: 'Casual players, beginners to basketball' },
            { id: 2, name: 'Competitive', description: 'Experienced players who play regularly' },
            { id: 3, name: 'Professional', description: 'High-level players with professional experience' }
          ];
        }

        // Fetch playing positions
        let playingPositionsResponse: any;
        try {
          playingPositionsResponse = await publicPlayingPositionsApi.getAll();
        } catch (error) {
          console.log('Playing positions endpoint failed, using hardcoded values');
          playingPositionsResponse = [
            { id: 1, name: 'Point Guard', abbreviation: 'PG' },
            { id: 2, name: 'Shooting Guard', abbreviation: 'SG' },
            { id: 3, name: 'Small Forward', abbreviation: 'SF' },
            { id: 4, name: 'Power Forward', abbreviation: 'PF' },
            { id: 5, name: 'Center', abbreviation: 'C' },
            { id: 6, name: 'Sixth Man', abbreviation: '6th' }
          ];
        }
        
        // Handle member types response
        let types: MemberType[] = [];
        if (Array.isArray(memberTypesResponse)) {
          types = memberTypesResponse;
        } else if (memberTypesResponse && Array.isArray(memberTypesResponse.data)) {
          types = memberTypesResponse.data;
        } else if (memberTypesResponse && memberTypesResponse.status === 'success' && Array.isArray(memberTypesResponse.data)) {
          types = memberTypesResponse.data;
        } else {
          console.warn('Unexpected member types response format:', memberTypesResponse);
          types = [];
        }
        
        // Handle playing levels response
        let levels: any[] = [];
        if (Array.isArray(playingLevelsResponse)) {
          levels = playingLevelsResponse.filter(level => level.is_active !== false);
        } else if (playingLevelsResponse && Array.isArray(playingLevelsResponse.data)) {
          levels = playingLevelsResponse.data.filter((level: any) => level.is_active !== false);
        } else {
          console.warn('Unexpected playing levels response format:', playingLevelsResponse);
          levels = [];
        }

        // Handle playing positions response
        let positions: any[] = [];
        if (Array.isArray(playingPositionsResponse)) {
          positions = playingPositionsResponse;
        } else if (playingPositionsResponse && Array.isArray(playingPositionsResponse.data)) {
          positions = playingPositionsResponse.data;
        } else {
          console.warn('Unexpected playing positions response format:', playingPositionsResponse);
          positions = [];
        }
        
        // Sort playing levels by sort_order if available
        levels.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
        
        console.log('Processed member types:', types);
        console.log('Processed playing levels:', levels);
        console.log('Processed playing positions:', positions);
        
        setMemberTypes(types);
        setPlayingLevels(levels);
        setPlayingPositions(positions);
      } catch (error) {
        console.error("Failed to fetch registration data", error);
        setMemberTypes([]);
        setPlayingLevels([
          { id: 1, name: 'Recreational', description: 'Casual players, beginners to basketball' },
          { id: 2, name: 'Competitive', description: 'Experienced players who play regularly' },
          { id: 3, name: 'Professional', description: 'High-level players with professional experience' }
        ]);
        setPlayingPositions([
          { id: 1, name: 'Point Guard', abbreviation: 'PG' },
          { id: 2, name: 'Shooting Guard', abbreviation: 'SG' },
          { id: 3, name: 'Small Forward', abbreviation: 'SF' },
          { id: 4, name: 'Power Forward', abbreviation: 'PF' },
          { id: 5, name: 'Center', abbreviation: 'C' },
          { id: 6, name: 'Sixth Man', abbreviation: '6th' }
        ]);
        toast({
          title: "Error",
          description: "Could not load all registration options. Using default values.",
          variant: "destructive",
        });
      } finally {
        setIsTypesLoading(false);
      }
    };
    fetchData();
  }, []);

  const handlePhotoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const onSubmit = async (data: z.infer<typeof registerSchema>) => {
    try {
      setIsLoading(true);
      
      // Convert form data to a regular object
      const formData = {
        ...data,
        terms_accepted: Boolean(data.terms_accepted),
        auto_payment: Boolean(data.auto_payment),
        consent: Boolean(data.consent)
      };
      
      // Debug: Log the submission data
      console.log('Submitting registration data:', formData);
      console.log('Terms accepted:', formData.terms_accepted, typeof formData.terms_accepted);
      
      // Handle photo separately if needed
      if (photoFile) {
        const photoFormData = new FormData();
        photoFormData.append('photo', photoFile);
        // You might want to handle photo upload separately here
      }

      await registerUser(formData);
      navigate('/dashboard');
      toast({
        title: "Success",
        description: "Registration successful! Welcome to the club.",
      });
    } catch (error: any) {
      console.error('Registration error:', error);
      toast({
        title: "Error",
        description: error.message || "Registration failed. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Section field groupings for validation
  const sectionFields = [
    [
      'first_name', 'last_name', 'email', 'phone', 'dob', 'nationality', 'id_passport', 'residential_address', 'occupation', 'employer',
    ] as const,
    [
      'member_type_id', 'membership_type', 'playing_level', 'playing_position_id', 'jersey_number',
    ] as const,
    [
      'emergency_contact_name', 'emergency_contact_phone', 'emergency_contact_relationship',
    ] as const,
    [
      'medical_info', 'health_conditions', 'allergies', 'medical_conditions', 'insurance', 'insurance_provider',
    ] as const,
    [
      'terms_accepted', 'consent',
    ] as const,
  ];

  const nextStep = async () => {
    if (step < 5) {
      const valid = await trigger(sectionFields[step] as unknown as Parameters<typeof trigger>[0]);
      if (!valid) return;
      setStep((s) => Math.min(s + 1, 5));
    }
  };
  const prevStep = () => setStep((s) => Math.max(s - 1, 0));

  // PDF Download logic
  const handleDownloadPDF = async () => {
    const input = document.getElementById('member-form-preview');
    if (!input) return;
    const canvas = await html2canvas(input, { scale: 2 });
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgProps = pdf.getImageProperties(imgData);
    const pdfWidth = pageWidth;
    const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
    pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight > pageHeight ? pageHeight : pdfHeight);
    pdf.save(`OG-membership-form-${getValues('first_name') || 'member'}.pdf`);
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center animated-gradient-background p-4 md:p-6">
      <OgLogoHeader />
      <div 
        className={`w-full ${step === 5 ? 'max-w-4xl' : 'max-w-xl'} transition-all duration-300`}
      >
        <div className="bg-white rounded-xl shadow-lg p-6 md:p-8">
          {/* Stepper/progress bar */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
              <span className="text-lg font-semibold text-basketball-blue-700">Step {step + 1} of 6</span>
              <span className="text-sm text-gray-500 mt-1 sm:mt-0">{stepLabels[step]}</span>
            </div>
            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-2 bg-gradient-to-r from-orange-400 to-basketball-blue-500 transition-all duration-500" 
                style={{ width: `${((step + 1) / 6) * 100}%` }}
              ></div>
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Section content */}
            {step === 0 && (
              <div>
                <div className="flex flex-col items-center space-y-4 mb-6">
                  <div className="relative">
                    <Avatar className="h-32 w-32">
                      {photoPreview ? (
                        <AvatarImage src={photoPreview} alt="Profile preview" className="object-cover" />
                      ) : (
                        <AvatarFallback>
                          <span className="text-2xl">📷</span>
                        </AvatarFallback>
                      )}
                    </Avatar>
                    <div className="absolute bottom-0 right-0">
                      <label
                        htmlFor="photo-upload"
                        className="flex items-center justify-center h-8 w-8 bg-basketball-orange-500 rounded-full cursor-pointer hover:bg-basketball-orange-600 transition-colors"
                      >
                        <Camera className="h-4 w-4 text-white" />
                      </label>
                      <input
                        id="photo-upload"
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoChange}
                        className="hidden"
                      />
                    </div>
                  </div>
                  <p className="text-sm text-gray-500">Upload your profile photo</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="first_name">First Name *</Label>
                    <Controller
                      name="first_name"
                      control={control}
                      render={({ field }) => <Input id="first_name" placeholder="John" {...field} />} 
                    />
                    {errors.first_name && <p className="text-red-500 text-sm">{errors.first_name.message}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="last_name">Last Name *</Label>
                    <Controller
                      name="last_name"
                      control={control}
                      render={({ field }) => <Input id="last_name" placeholder="Doe" {...field} />} 
                    />
                    {errors.last_name && <p className="text-red-500 text-sm">{errors.last_name.message}</p>}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Controller
                      name="email"
                      control={control}
                      render={({ field }) => <Input id="email" type="email" placeholder="john.doe@example.com" {...field} />} 
                    />
                    {errors.email && <p className="text-red-500 text-sm">{errors.email.message}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Controller
                      name="phone"
                      control={control}
                      render={({ field }) => <Input id="phone" placeholder="+255 123 456 789" {...field} />} 
                    />
                    {errors.phone && <p className="text-red-500 text-sm">{errors.phone.message}</p>}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="dob">Date of Birth</Label>
                    <Controller
                      name="dob"
                      control={control}
                      render={({ field }) => (
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant={"outline"}
                              className={cn(
                                "w-full justify-start text-left font-normal",
                                !field.value && "text-muted-foreground"
                              )}
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {field.value ? format(new Date(field.value), "PPP") : <span>Pick a date</span>}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={field.value ? new Date(field.value) : undefined}
                              onSelect={(date) => field.onChange(date ? format(date, 'yyyy-MM-dd') : '')}
                              disabled={(date) =>
                                date > new Date() || date < new Date('1900-01-01')
                              }
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      )}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="nationality">Nationality</Label>
                    <Controller
                      name="nationality"
                      control={control}
                      render={({ field }) => <Input id="nationality" placeholder="Tanzanian" {...field} />} 
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="id_passport">ID/Passport Number</Label>
                    <Controller
                      name="id_passport"
                      control={control}
                      render={({ field }) => <Input id="id_passport" placeholder="ID123456" {...field} />} 
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="occupation">Occupation</Label>
                    <Controller
                      name="occupation"
                      control={control}
                      render={({ field }) => <Input id="occupation" placeholder="Software Engineer" {...field} />} 
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="residential_address">Residential Address</Label>
                  <Controller
                    name="residential_address"
                    control={control}
                    render={({ field }) => <Textarea id="residential_address" placeholder="Enter your address" {...field} />} 
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="employer">Employer</Label>
                  <Controller
                    name="employer"
                    control={control}
                    render={({ field }) => <Input id="employer" placeholder="Company Name" {...field} />} 
                  />
                </div>

                <div className="space-y-4 mt-6">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="text-sm font-medium text-gray-900">Account Security</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Choose a strong password for your account
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="password">Password *</Label>
                      <Controller
                        name="password"
                        control={control}
                        render={({ field }) => (
                          <Input 
                            id="password" 
                            type="password" 
                            {...field}
                            className={errors.password ? "border-red-500" : ""}
                          />
                        )}
                      />
                      {errors.password && <p className="text-red-500 text-sm">{errors.password.message}</p>}
                      <p className="text-xs text-gray-500">Must be at least 8 characters long</p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="password_confirmation">Confirm Password *</Label>
                      <Controller
                        name="password_confirmation"
                        control={control}
                        render={({ field }) => (
                          <Input 
                            id="password_confirmation" 
                            type="password" 
                            {...field}
                            className={errors.password_confirmation ? "border-red-500" : ""}
                          />
                        )}
                      />
                      {errors.password_confirmation && <p className="text-red-500 text-sm">{errors.password_confirmation.message}</p>}
                    </div>
                  </div>
                </div>
              </div>
            )}
            {step === 1 && (
              <div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Label htmlFor="member_type_id">Membership Type *</Label>
                      <HoverCard>
                        <HoverCardTrigger asChild>
                          <InfoCircledIcon className="h-4 w-4 text-gray-500 cursor-help" />
                        </HoverCardTrigger>
                        <HoverCardContent className="w-80">
                          <div className="space-y-2">
                            <h4 className="font-medium">Membership Types</h4>
                            <div className="text-sm text-gray-500 space-y-1">
                              {memberTypes.map((type) => (
                                <div key={type.id} className="space-y-1">
                                  <div className="font-medium">{type.name}</div>
                                  <div>Monthly Fee: {type.monthly_fee} TZS</div>
                                  {type.description && <div>{type.description}</div>}
                                </div>
                              ))}
                            </div>
                          </div>
                        </HoverCardContent>
                      </HoverCard>
                    </div>
                    <Controller
                      name="member_type_id"
                      control={control}
                      render={({ field }) => (
                        <Select
                          onValueChange={(value) => {
                            field.onChange(value);
                            const selectedType = memberTypes.find((type) => String(type.id) === value);
                            if (selectedType) {
                              setValue('membership_type', selectedType.name);
                            } else {
                              setValue('membership_type', '');
                            }
                          }}
                          defaultValue={field.value}
                          disabled={isTypesLoading}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={isTypesLoading ? "Loading types..." : "Select a type"} />
                          </SelectTrigger>
                          <SelectContent>
                            {memberTypes.map((type) => (
                              <SelectItem key={type.id} value={String(type.id)}>
                                {type.name} - {type.monthly_fee} TZS/month
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                    {errors.member_type_id && <p className="text-red-500 text-sm">{errors.member_type_id.message}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="playing_level">Playing Level</Label>
                    <Controller
                      name="playing_level"
                      control={control}
                      render={({ field }) => (
                        <Select onValueChange={field.onChange} defaultValue={field.value} disabled={isTypesLoading}>
                          <SelectTrigger>
                            <SelectValue placeholder={isTypesLoading ? "Loading levels..." : "Select a level"} />
                          </SelectTrigger>
                          <SelectContent>
                            {playingLevels.map((level) => (
                              <SelectItem key={level.id} value={level.name}>
                                {level.name}
                                {level.description && (
                                  <span className="text-xs text-gray-500 block">{level.description}</span>
                                )}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="playing_position_id">Playing Position</Label>
                    <Controller
                      name="playing_position_id"
                      control={control}
                      render={({ field }) => (
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a position" />
                          </SelectTrigger>
                          <SelectContent>
                            {playingPositions.map((position) => (
                              <SelectItem key={position.id} value={String(position.id)}>
                                {position.name} ({position.abbreviation})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="jersey_number">Preferred Jersey Number</Label>
                    <Controller
                      name="jersey_number"
                      control={control}
                      render={({ field }) => <Input id="jersey_number" type="number" min="1" max="99" placeholder="23" {...field} />} 
                    />
                    {errors.jersey_number && <p className="text-red-500 text-sm">{errors.jersey_number.message}</p>}
                  </div>
                </div>
              </div>
            )}
            {step === 2 && (
              <div>
                <div className="space-y-2">
                  <Label htmlFor="emergency_contact_name">Emergency Contact Name</Label>
                  <Controller
                    name="emergency_contact_name"
                    control={control}
                    render={({ field }) => <Input id="emergency_contact_name" placeholder="Jane Doe" {...field} />} 
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="emergency_contact_phone">Emergency Contact Phone</Label>
                  <Controller
                    name="emergency_contact_phone"
                    control={control}
                    render={({ field }) => <Input id="emergency_contact_phone" placeholder="+255 123 456 789" {...field} />} 
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="emergency_contact_relationship">Relationship to Emergency Contact</Label>
                  <Controller
                    name="emergency_contact_relationship"
                    control={control}
                    render={({ field }) => <Input id="emergency_contact_relationship" placeholder="Spouse" {...field} />} 
                  />
                </div>
              </div>
            )}
            {step === 3 && (
              <div>
                <div className="space-y-2">
                  <Label htmlFor="medical_info">Medical Information</Label>
                  <Controller
                    name="medical_info"
                    control={control}
                    render={({ field }) => <Textarea id="medical_info" placeholder="Any relevant medical information" {...field} />} 
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="health_conditions">Health Conditions</Label>
                  <Controller
                    name="health_conditions"
                    control={control}
                    render={({ field }) => <Textarea id="health_conditions" placeholder="List any health conditions" {...field} />} 
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="allergies">Allergies</Label>
                  <Controller
                    name="allergies"
                    control={control}
                    render={({ field }) => <Textarea id="allergies" placeholder="List any allergies" {...field} />} 
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="insurance">Insurance Information</Label>
                    <Controller
                      name="insurance"
                      control={control}
                      render={({ field }) => <Input id="insurance" placeholder="Insurance details" {...field} />} 
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="insurance_provider">Insurance Provider</Label>
                    <Controller
                      name="insurance_provider"
                      control={control}
                      render={({ field }) => <Input id="insurance_provider" placeholder="Provider name" {...field} />} 
                    />
                  </div>
                </div>
                </div>
            )}
            {step === 4 && (
              <div>
                <div className="space-y-4 mt-6">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="text-sm font-medium text-gray-900">Terms & Conditions</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Please read and accept our terms and conditions
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center space-x-2">
                      <Controller
                        name="terms_accepted"
                        control={control}
                        render={({ field }) => (
                          <Checkbox
                            id="terms"
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        )}
                      />
                      <Label htmlFor="terms" className="text-sm">
                        I accept the <Link to="/terms" target="_blank" className="underline hover:text-basketball-orange-500">terms and conditions</Link>
                      </Label>
                    </div>
                    {errors.terms_accepted && <p className="text-red-500 text-sm">{errors.terms_accepted.message}</p>}

                    <div className="flex items-center space-x-2">
                      <Controller
                        name="consent"
                        control={control}
                        render={({ field }) => (
                          <Checkbox
                            id="consent"
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        )}
                      />
                      <Label htmlFor="consent" className="text-sm">
                        I give consent to proceed
                      </Label>
                    </div>
                    {errors.consent && <p className="text-red-500 text-sm">{errors.consent.message}</p>}
                  </div>
                </div>
              </div>
            )}
            {step === 5 && (
              <div className="flex flex-col items-center space-y-6">
                <div className="w-full max-w-3xl mx-auto bg-gray-50 rounded-lg overflow-hidden">
                  {/* Form Preview Container */}
                  <div className="relative">
                    {/* Preview Header */}
                    <div className="bg-basketball-blue-700 text-white py-4 px-6 text-center">
                      <h3 className="text-lg font-semibold">Membership Application Preview</h3>
                      <p className="text-sm opacity-80">Review your information before submitting</p>
                    </div>
                    
                    {/* Scrollable Preview Content */}
                    <div className="overflow-y-auto max-h-[calc(100vh-400px)] p-6">
                      <div id="member-form-preview" className="bg-white border border-gray-200 rounded-lg shadow-sm">
                        <MemberFormTemplate data={getValues()} showClubSection={true} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Download Button */}
                <Button 
                  type="button" 
                  onClick={handleDownloadPDF} 
                  className="flex items-center space-x-2 bg-basketball-blue-600 hover:bg-basketball-blue-700 text-white px-6 py-2 rounded-lg shadow-sm transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                  <span>Download PDF</span>
                </Button>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-4 border-t border-gray-200">
              <Button 
                type="button" 
                onClick={prevStep} 
                disabled={step === 0} 
                className="px-6 py-2 rounded-lg font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Back
              </Button>
              
              {step < 5 && (
                <Button 
                  type="button" 
                  onClick={nextStep} 
                  className="px-6 py-2 rounded-lg font-semibold bg-gradient-to-r from-orange-400 to-basketball-blue-500 text-white hover:from-orange-500 hover:to-basketball-blue-600 transition-all duration-300"
                >
                  Next
                </Button>
              )}
              
              {step === 5 && (
                <Button 
                  type="submit" 
                  className="px-6 py-2 rounded-lg font-semibold bg-gradient-to-r from-orange-400 to-basketball-blue-500 text-white hover:from-orange-500 hover:to-basketball-blue-600 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed" 
                  disabled={isLoading || !getValues('terms_accepted') || !getValues('consent')}
                >
                  {isLoading ? <LoadingSpinner /> : 'Register'}
                </Button>
              )}
            </div>
          </form>
        </div>
        
        {/* Sign in link */}
        <div className="mt-6 text-center text-sm">
          Already have an account?{' '}
          <Link to="/login" className="text-basketball-orange-500 hover:text-basketball-blue-500 underline font-medium">
            Sign in here
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Register;
